// How owners get paid (0097; Liam, 29 Oct 2026; designs E + F on https://claude.ai/artifact/EqUcAcKbAFKUHj73jmp9gh;
// docs/plans/owner-pay/PLAN.md). The rules only, no page: what each owner or partner is due for a day, worked out from the
// round's setting, so the close-out can fill it in. Every amount is a suggestion; what is typed is what counts.
//
//   keep   the owner keeps what is left after workers: nobody to add
//   wage   a day rate for each owner on a day they work (a month wage is taken off once a month, never here)
//   split  each partner out working that day: their percent of what is left after workers, or of the whole day, at least
//          their minimum (Liam: "if im not out then my minimum isnt 200 its only when im out")
//   ask    each owner out working that day types what they took, nothing filled in

export interface OwnerPaySetting { id: number | null; mode: 'keep' | 'wage' | 'split' | 'ask'; base: 'after' | 'all'; wage_kind: 'day' | 'month' | null; wage_pence: number | null; version: number }
export interface PartnerRow { id: number; name: string; pct: number; min_pence: number | null }
export interface TakeRow { id: number; version: number; period: string; payee: string; category: 'share' | 'wage' | 'took'; rule: string | null; amount_pence: number; paid_at: string | null; paid_how: 'cash' | 'bank' | null }
export interface Payee { name: string; category: TakeRow['category']; suggested: number | null; rule: string | null; why: string; take: TakeRow | null }

// The day as the rules need it: who was out working, who is an owner, the day's work and what the workers are paid.
export interface DayForPay { people: { worker: string; is_owner: boolean; out: boolean }[]; work: number; workerPay: number }

const lower = (s: string): string => s.trim().toLowerCase()
const pounds = (p: number): string => `£${(p / 100).toFixed(2).replace(/\.00$/, '')}`

export function payeesOf(setting: OwnerPaySetting | null | undefined, partners: readonly PartnerRow[], takes: readonly TakeRow[], day: DayForPay): Payee[] {
  const out: Payee[] = []
  const takeOf = (name: string): TakeRow | null => takes.find((t) => lower(t.payee) === lower(name)) ?? null
  const isOut = (name: string): boolean => day.people.some((p) => p.out && lower(p.worker) === lower(name))
  const s = setting ?? { mode: 'keep' as const }
  if (s.mode === 'wage' && setting?.wage_kind === 'day' && setting.wage_pence !== null) {
    for (const p of day.people.filter((x) => x.is_owner && x.out)) out.push({ name: p.worker, category: 'wage', suggested: setting.wage_pence, rule: `wage day ${setting.wage_pence}`, why: `Your day rate, ${pounds(setting.wage_pence)}`, take: takeOf(p.worker) })
  }
  if (s.mode === 'split' && setting) {
    const after = setting.base !== 'all'
    const base = Math.max(0, after ? day.work - day.workerPay : day.work)
    // Each partner's percent in whole pence, the odd pence to the largest remainders, so the shares never come to more
    // than their percents of the day together (Astra: two 50% halves of £100.01 made £100.02).
    const outPartners = partners.filter((x) => isOut(x.name))
    const raw = outPartners.map((x) => (base * x.pct) / 100)
    const cuts = raw.map(Math.floor)
    let spare = Math.round(raw.reduce((n, r) => n + r, 0)) - cuts.reduce((n, c) => n + c, 0)
    for (const i of raw.map((r, i) => [r - Math.floor(r), i] as const).sort((a, b) => b[0] - a[0]).map(([, i]) => i)) { if (spare <= 0) break; cuts[i]! += 1; spare -= 1 }
    for (const partner of partners) {
      if (!isOut(partner.name)) continue
      const cut = cuts[outPartners.indexOf(partner)]!
      const min = day.work > 0 && partner.min_pence ? partner.min_pence : 0
      const due = Math.max(cut, min)
      const of = after ? `what is left after workers, ${pounds(base)}` : `the day's work, ${pounds(base)}`
      out.push({ name: partner.name, category: 'share', suggested: due, rule: `share ${partner.pct}${after ? '' : ' all'}${partner.min_pence ? ` min ${partner.min_pence}` : ''}`,
        why: `${partner.pct}% of ${of}${min > cut ? `; at least ${pounds(min)}` : ''}`, take: takeOf(partner.name) })
    }
  }
  if (s.mode === 'ask') {
    for (const p of day.people.filter((x) => x.is_owner && x.out)) {
      const t = takeOf(p.worker)
      out.push({ name: p.worker, category: 'took', suggested: null, rule: null, why: 'What you took for today', take: t })
    }
  }
  // Something already saved for someone the setting no longer lists (it was changed since): still shown, to change or undo.
  for (const t of takes) if (!out.some((p) => lower(p.name) === lower(t.payee))) out.push({ name: t.payee, category: t.category, suggested: null, rule: t.rule, why: 'Saved before the setting changed', take: t })
  return out
}

// What the owners come to for the day: what was saved, else what is suggested.
export const ownersTotal = (payees: readonly Payee[]): number => payees.reduce((n, p) => n + (p.take?.amount_pence ?? p.suggested ?? 0), 0)
// Every owner's amount is decided (saved, taken or not) once the setting asks for one.
export const ownersDecided = (payees: readonly Payee[]): boolean => payees.every((p) => !!p.take)
